import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MainLayout } from '../../components/MainLayout';
import api from '../../lib/api';
import { formatDuration } from '../../lib/utils';
import {
  Card, CardContent, CardHeader, CardTitle,
  Button, Tabs, TabsContent, TabsList, TabsTrigger, Badge,
} from '@werkstatt/ui';
import { ChevronLeft, ChevronRight, Download } from 'lucide-react';
import { cn } from '../../lib/utils';

function getISOWeek(date: Date): string {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  return `${d.getUTCFullYear()}-W${weekNo.toString().padStart(2, '0')}`;
}

interface EmployeeStat {
  employee: { id: string; first_name: string; last_name: string; department?: string; hours_per_week: number };
  total_minutes: number;
  target_minutes: number;
  overtime_minutes: number;
  days_present: number;
  work_days: number;
  absence_count: number;
}

export default function Reports() {
  const today = new Date();
  const [week, setWeek] = useState(getISOWeek(today));
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [year, setYear] = useState(today.getFullYear());

  const { data: weeklyData, isLoading: weekLoading } = useQuery({
    queryKey: ['report-weekly', week],
    queryFn: async () => {
      const res = await api.get(`/reports/weekly?week=${week}`);
      return res.data as { week: string; date_from: string; date_to: string; employees: EmployeeStat[] };
    },
  });

  const { data: monthlyData, isLoading: monthLoading } = useQuery({
    queryKey: ['report-monthly', year, month],
    queryFn: async () => {
      const res = await api.get(`/reports/monthly?year=${year}&month=${month}`);
      return res.data as { year: number; month: number; date_from: string; date_to: string; employees: EmployeeStat[] };
    },
  });

  const navigateWeek = (delta: number) => {
    const [y, w] = week.split('-W').map(Number);
    const d = new Date(y, 0, 1 + (w - 1) * 7);
    d.setDate(d.getDate() + delta * 7);
    setWeek(getISOWeek(d));
  };

  const navigateMonth = (delta: number) => {
    let m = month + delta;
    let y = year;
    if (m > 12) { m = 1; y++; }
    if (m < 1) { m = 12; y--; }
    setMonth(m);
    setYear(y);
  };

  const monthNames = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];

  const StatRow = ({ stat }: { stat: EmployeeStat }) => {
    const diff = stat.overtime_minutes;
    const isPos = diff >= 0;
    return (
      <tr className="border-b hover:bg-muted/30">
        <td className="py-3 pr-4 font-medium">{stat.employee.last_name}, {stat.employee.first_name}</td>
        <td className="py-3 pr-4 text-muted-foreground">{stat.employee.department || '—'}</td>
        <td className="py-3 pr-4">{stat.days_present} / {stat.work_days}</td>
        <td className="py-3 pr-4 text-muted-foreground">{formatDuration(stat.target_minutes)}</td>
        <td className="py-3 pr-4">{formatDuration(stat.total_minutes)}</td>
        <td className="py-3 pr-4">
          <span className={cn("font-medium", isPos ? "text-green-600" : "text-red-600")}>
            {isPos ? '+' : ''}{formatDuration(Math.abs(diff))}
          </span>
        </td>
        <td className="py-3 pr-4">
          {stat.absence_count > 0 ? (
            <Badge variant="secondary">{stat.absence_count}</Badge>
          ) : '—'}
        </td>
      </tr>
    );
  };

  const TotalRow = ({ stats }: { stats: EmployeeStat[] }) => {
    const total = stats.reduce((s, e) => ({
      total_minutes: s.total_minutes + e.total_minutes,
      target_minutes: s.target_minutes + e.target_minutes,
      overtime_minutes: s.overtime_minutes + e.overtime_minutes,
      days_present: s.days_present + e.days_present,
    }), { total_minutes: 0, target_minutes: 0, overtime_minutes: 0, days_present: 0 });
    const diff = total.overtime_minutes;
    return (
      <tr className="border-t font-semibold bg-muted/30">
        <td className="py-3 pr-4" colSpan={2}>Gesamt ({stats.length} MA)</td>
        <td className="py-3 pr-4">{total.days_present}</td>
        <td className="py-3 pr-4">{formatDuration(total.target_minutes)}</td>
        <td className="py-3 pr-4">{formatDuration(total.total_minutes)}</td>
        <td className="py-3 pr-4">
          <span className={diff >= 0 ? "text-green-600" : "text-red-600"}>
            {diff >= 0 ? '+' : ''}{formatDuration(Math.abs(diff))}
          </span>
        </td>
        <td className="py-3 pr-4"></td>
      </tr>
    );
  };

  const handleCsvExport = () => {
    window.open(`/api/reports/export/csv?year=${year}&month=${month}`, '_blank');
  };

  return (
    <MainLayout>
      <div className="space-y-6">
        <div>
          <h1 className="font-display text-3xl font-bold">Berichte</h1>
          <p className="text-muted-foreground mt-1">Wöchentliche & monatliche Auswertungen</p>
        </div>

        <Tabs defaultValue="weekly">
          <TabsList className="grid w-full grid-cols-2 max-w-sm">
            <TabsTrigger value="weekly">Wochenbericht</TabsTrigger>
            <TabsTrigger value="monthly">Monatsbericht</TabsTrigger>
          </TabsList>

          <TabsContent value="weekly" className="space-y-4 mt-4">
            <Card variant="glass">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="font-display">Wochenbericht</CardTitle>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => navigateWeek(-1)}>
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm font-medium min-w-32 text-center">
                      {week}
                      {weeklyData && ` (${weeklyData.date_from} – ${weeklyData.date_to})`}
                    </span>
                    <Button variant="outline" size="sm" onClick={() => navigateWeek(1)}>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {weekLoading ? (
                  <p className="text-muted-foreground text-sm py-8 text-center">Lade...</p>
                ) : !weeklyData?.employees?.length ? (
                  <p className="text-muted-foreground text-sm py-8 text-center">Keine Daten für diese Woche</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-left">
                          <th className="py-3 pr-4 font-medium">Name</th>
                          <th className="py-3 pr-4 font-medium">Abteilung</th>
                          <th className="py-3 pr-4 font-medium">Anwesend (Tage)</th>
                          <th className="py-3 pr-4 font-medium">Soll</th>
                          <th className="py-3 pr-4 font-medium">Ist</th>
                          <th className="py-3 pr-4 font-medium">Differenz</th>
                          <th className="py-3 pr-4 font-medium">Abwesenheiten</th>
                        </tr>
                      </thead>
                      <tbody>
                        {weeklyData.employees.map((s) => <StatRow key={s.employee.id} stat={s} />)}
                        <TotalRow stats={weeklyData.employees} />
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="monthly" className="space-y-4 mt-4">
            <Card variant="glass">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="font-display">Monatsbericht</CardTitle>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={() => navigateMonth(-1)}>
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <span className="text-sm font-medium min-w-36 text-center">
                        {monthNames[month - 1]} {year}
                      </span>
                      <Button variant="outline" size="sm" onClick={() => navigateMonth(1)}>
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                    <Button variant="outline" size="sm" onClick={handleCsvExport}>
                      <Download className="h-4 w-4 mr-2" />
                      CSV Export
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {monthLoading ? (
                  <p className="text-muted-foreground text-sm py-8 text-center">Lade...</p>
                ) : !monthlyData?.employees?.length ? (
                  <p className="text-muted-foreground text-sm py-8 text-center">Keine Daten für diesen Monat</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-left">
                          <th className="py-3 pr-4 font-medium">Name</th>
                          <th className="py-3 pr-4 font-medium">Abteilung</th>
                          <th className="py-3 pr-4 font-medium">Anwesend (Tage)</th>
                          <th className="py-3 pr-4 font-medium">Soll</th>
                          <th className="py-3 pr-4 font-medium">Ist</th>
                          <th className="py-3 pr-4 font-medium">Überstunden</th>
                          <th className="py-3 pr-4 font-medium">Abwesenheiten</th>
                        </tr>
                      </thead>
                      <tbody>
                        {monthlyData.employees.map((s) => <StatRow key={s.employee.id} stat={s} />)}
                        <TotalRow stats={monthlyData.employees} />
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </MainLayout>
  );
}
